import {
  LOTS,
  lotsForDomain,
  lotsForPpa,
} from "@/compliance_groups/fixture/allocator";
import {
  DOMAIN_INPUTS,
  PPA_INPUTS,
  STATEMENTS,
} from "@/compliance_groups/fixture/complianceGroupsFixture";
import {
  initialDemoState,
  type CompanyEdit,
  type ConsumptionEntry,
  type DemoState,
  type DomainKey,
  type GroupKey,
  type MeterRecord,
  type PpaKey,
  type StatementFixture,
} from "@/compliance_groups/fixture/complianceGroupsTypes";
import {
  deriveDomain,
  derivePpa,
  deriveRollup,
  type DerivedDomain,
  type DerivedPpa,
  type GroupRollup,
} from "@/compliance_groups/fixture/derive";
import { mapValues } from "lodash-es";
import { defineStore } from "pinia";
import { computed, reactive, readonly } from "vue";

export interface LocationFormSubmit {
  isNew: boolean;
  added: boolean;
  id: string;
  companyName: string;
  record: MeterRecord;
}

export interface LocationRef {
  id: string;
  added: boolean;
}

/** One confirmed reading as the Report tab places it: a location's month, in MWh. */
export interface ConfirmedReading {
  meterId: string;
  /** 0-based month index of the demo year. */
  month: number;
  mwh: number;
}

/**
 * Mutable state of the Compliance Groups demo. Everything on screen is derived from the static
 * fixture plus this state, so one edit here moves every dependent view. Nothing touches the
 * backend; the state is in-memory only and resets on reload.
 */
export const useComplianceDemoStore = defineStore("complianceDemo", () => {
  const state = reactive<DemoState>(initialDemoState());

  // PERSISTENCE SEAM. The demo intentionally resets on reload. If edits ever need to survive a
  // refresh, implement these two against sessionStorage (or VueUse `useStorage`): hydrate() reads a
  // serialised DemoState into `state`, persist() writes it, and a `watch(state, persist, { deep:
  // true })` below keeps them in sync. Nothing else in the feature needs to change.
  const hydrate = (): void => {};
  const persist = (): void => {};
  hydrate();

  const domains = computed<Record<DomainKey, DerivedDomain>>(() =>
    mapValues(DOMAIN_INPUTS, (input) =>
      deriveDomain(input, lotsForDomain(LOTS, input.key), state)
    )
  );
  const ppas = computed<Record<PpaKey, DerivedPpa>>(() =>
    mapValues(PPA_INPUTS, (input) =>
      derivePpa(input, lotsForPpa(LOTS, input.key))
    )
  );
  const rollups = computed<Record<GroupKey, GroupRollup>>(() => {
    const ds = Object.values(domains.value);
    const ps = Object.values(ppas.value);
    const forGroup = (g: GroupKey) =>
      deriveRollup(
        g,
        ds.filter((d) => d.group === g),
        ps.filter((p) => p.group === g),
        state
      );
    return { eecs: forGroup("eecs"), rego: forGroup("rego") };
  });

  /** Only the EECS group has executed cancellations in this demo. */
  const statementsFor = (group: GroupKey): StatementFixture[] =>
    group === "eecs" ? STATEMENTS : [];

  /**
   * Place confirmed readings: each becomes that location-month's actual value. Later
   * confirmations for the same location-month replace earlier ones.
   */
  const applyReadings = (dk: DomainKey, rows: ConfirmedReading[]): void => {
    const domainReadings = { ...(state.readings[dk] ?? {}) };
    for (const row of rows) {
      domainReadings[row.meterId] = {
        ...(domainReadings[row.meterId] ?? {}),
        [row.month]: row.mwh,
      };
    }
    state.readings[dk] = domainReadings;
  };
  const submitRequest = (): void => {
    state.requestSubmitted = true;
  };
  const setCertMonthFilter = (month: number | null): void => {
    state.certMonthFilter = month;
  };
  const toggleCertMonthFilter = (month: number): void => {
    state.certMonthFilter = state.certMonthFilter === month ? null : month;
  };
  const setCompanyField = (
    dk: DomainKey,
    companyKey: string,
    field: keyof CompanyEdit,
    value: string
  ): void => {
    const forDomain = state.companyEdits[dk] ?? {};
    forDomain[companyKey] = {
      ...(forDomain[companyKey] ?? {}),
      [field]: value,
    };
    state.companyEdits[dk] = forDomain;
  };
  const saveLocation = (dk: DomainKey, submit: LocationFormSubmit): void => {
    const record: MeterRecord = {
      name: submit.record.name.trim() || "Untitled consumption location",
      mpid: submit.record.mpid,
      supplier: submit.record.supplier.trim(),
      months: submit.record.months,
    };
    if (submit.isNew) {
      const list = state.metersAdded[dk] ?? [];
      state.metersAdded[dk] = [
        ...list,
        {
          uid: `new-${Date.now()}-${list.length}`,
          companyName: submit.companyName,
          ...record,
        },
      ];
      return;
    }
    if (submit.added) {
      state.metersAdded[dk] = (state.metersAdded[dk] ?? []).map((a) =>
        a.uid === submit.id ? { ...a, ...record } : a
      );
      return;
    }
    state.meterEdits[dk] = {
      ...(state.meterEdits[dk] ?? {}),
      [submit.id]: record,
    };
  };
  const removeLocation = (dk: DomainKey, ref: LocationRef): void => {
    if (ref.added) {
      state.metersAdded[dk] = (state.metersAdded[dk] ?? []).filter(
        (a) => a.uid !== ref.id
      );
      return;
    }
    state.metersRemoved[dk] = [...(state.metersRemoved[dk] ?? []), ref.id];
  };
  const setConsumptionEntries = (
    dk: DomainKey,
    entries: ConsumptionEntry[]
  ): void => {
    state.consumptionEntries[dk] = entries;
  };
  const reset = (): void => {
    Object.assign(state, initialDemoState());
  };

  return {
    state: readonly(state),
    lots: LOTS,
    domains,
    ppas,
    rollups,
    statementsFor,
    applyReadings,
    submitRequest,
    setCertMonthFilter,
    toggleCertMonthFilter,
    setCompanyField,
    saveLocation,
    removeLocation,
    setConsumptionEntries,
    reset,
    hydrate,
    persist,
  };
});
